import { Controller } from "@hotwired/stimulus"
import { debounce, fetchWithCsrf } from "../util"

export default class extends Controller {
  static targets = ["item", "container", "handler"]
  static values = {
    url: String,
    idsParam: String
  }

  connect() {
    this.bindMethods()
    this.setupDragAndDrop()
  }

  bindMethods() {
    this.handleDragOver = this.handleDragOver.bind(this)
    this.handleDrop = this.handleDrop.bind(this)
    this.handleDragStart = this.handleDragStart.bind(this)
    this.handleDragEnd = this.handleDragEnd.bind(this)
    this.debouncedReorderItems = debounce(this.reorderItems.bind(this), 100)
  }

  setupDragAndDrop() {
    this.setupHandlers()
    this.setupDropZones()
  }

  setupHandlers() {
    this.handlerTargets.forEach((handler, index) => {
      const item = this.itemTargets[index]
      if (!item) return

      handler.draggable = true
      handler.style.cursor = 'move'
      handler.dragItem = item
      handler.addEventListener('dragstart', this.handleDragStart)
      handler.addEventListener('dragend', this.handleDragEnd)
    })
  }

  setupDropZones() {
    this.itemTargets.forEach(item => {
      item.addEventListener('dragover', this.handleDragOver)
      item.addEventListener('drop', this.handleDrop)
    })

    this.containerTarget.addEventListener('dragover', this.handleDragOver)
    this.containerTarget.addEventListener('drop', this.handleDrop)
  }

  handleDragStart(event) {
    const item = event.target.dragItem
    if (!item) return

    this.initializeDragState(item, event.target)
    this.setupDataTransfer(event, item)
    this.createDragImage(event, item)
  }

  initializeDragState(item, handler) {
    this.draggedItem = item
    this.draggedHandler = handler
    item.classList.add('drag-item')
  }

  setupDataTransfer(event, item) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/html', item.outerHTML)
  }

  createDragImage(event, item) {
    const wrapper = this.createDragImageWrapper(item)
    const clone = this.createDragImageClone(item)

    wrapper.appendChild(clone)
    document.body.appendChild(wrapper)

    const dragOffset = this.calculateDragOffset(event, item)
    event.dataTransfer.setDragImage(wrapper, dragOffset.x, dragOffset.y)

    this.cleanupDragImage(wrapper)
  }

  createDragImageWrapper(item) {
    const wrapper = document.createElement('div')
    wrapper.classList.add('drag-image-wrapper')
    wrapper.style.width = `${item.offsetWidth}px`
    return wrapper
  }

  createDragImageClone(item) {
    const clone = item.cloneNode(true)
    clone.classList.remove('drag-item')
    clone.classList.add('drag-image')
    return clone
  }

  calculateDragOffset(event, item) {
    const handler = event.target
    const itemRect = item.getBoundingClientRect()
    const handlerRect = handler.getBoundingClientRect()

    const handlerCenterX = handlerRect.left + handlerRect.width / 0.8 - itemRect.left
    const handlerCenterY = handlerRect.top + handlerRect.height / 2 - itemRect.top

    return { x: handlerCenterX, y: handlerCenterY }
  }

  cleanupDragImage(wrapper) {
    setTimeout(() => {
      if (document.body.contains(wrapper)) {
        document.body.removeChild(wrapper)
      }
    }, 100)
  }

  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'

    if (!this.draggedItem) return

    const targetItem = this.getTargetItem(event)
    if (!targetItem) return

    this.repositionDraggedItem(event, targetItem)
  }

  getTargetItem(event) {
    const targetItem = event.target.closest('[data-drag-drop-target="item"]')
    return targetItem && targetItem !== this.draggedItem ? targetItem : null
  }

  repositionDraggedItem(event, targetItem) {
    const rect = targetItem.getBoundingClientRect()
    const midpoint = rect.top + rect.height / 2
    const insertBefore = event.clientY < midpoint

    if (insertBefore) {
      this.containerTarget.insertBefore(this.draggedItem, targetItem)
    } else {
      this.containerTarget.insertBefore(this.draggedItem, targetItem.nextSibling)
    }
  }

  handleDrop(event) {
    event.preventDefault()
    if (this.draggedItem) {
      this.debouncedReorderItems()
    }
  }

  handleDragEnd() {
    this.cleanupDragState()
  }

  cleanupDragState() {
    if (this.draggedItem) {
      this.draggedItem.classList.remove('drag-item')
    }
    this.draggedItem = null
    this.draggedHandler = null
  }

  reorderItems() {
    const itemIds = this.getItemIds()
    const formData = this.createFormData(itemIds)

    this.submitReorder(formData)
  }

  getItemIds() {
    return this.itemTargets.map(item => item.dataset.id)
  }

  createFormData(itemIds) {
    const formData = new FormData()
    const paramName = this.idsParamValue || 'item_ids'

    itemIds.forEach(id => {
      formData.append(`${paramName}[]`, id)
    })

    return formData
  }

  submitReorder(formData) {
    fetchWithCsrf(this.urlValue, {
      method: 'PATCH',
      body: formData,
      headers: {
        'Accept': 'text/vnd.turbo-stream.html'
      }
    })
    .then(response => response.text())
    .then(html => Turbo.renderStreamMessage(html))
    .catch(error => {
      console.error('Error reordering items:', error)
    })
  }
}
